import { useState, useEffect, SyntheticEvent } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, Divider, FormControl,
  TextField, InputLabel, MenuItem, Select, useMediaQuery,
  Checkbox, FormGroup, FormControlLabel, SelectChangeEvent, Paper, CircularProgress
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import SearchIcon from '@mui/icons-material/Search';
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import CartulinaModal from "./modals/ModalClientCartel";
import ModalCloveClients from "./modals/ModalCloveClients";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Interfaces
interface Office { id: number; name: string; }
interface Collect { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedCollects: number[];
}

// ← NUEVO: coincide con el payload del ejemplo
interface ResumenSaldoProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

const formatInt = (value: number): string =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value);

// Estilos
const SummaryBox = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[0],
  border: `1px solid ${theme.palette.divider}`,
}));

// Componente
const ResumenSaldo: React.FC<ResumenSaldoProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  document.title = "Resumen Saldo";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const { drawerOpen } = useDrawer();

  const [selectedIds, setSelectedIds] = useState<string[] | number[]>([]);

  // ⬇ estado del modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCollectId, setModalCollectId] = useState<number | null>(null);
  const [modalCollectName, setModalCollectName] = useState<string | null>(null);
  const [modalDate, setModalDate] = useState<string | null>(null);
  const [cloveModalOpen, setCloveModalOpen] = useState(false);
  const [cloveModalCollectId, setCloveModalCollectId] = useState<number | null>(null);
  const [cloveModalCollectName, setCloveModalCollectName] = useState<string | null>(null);
  const [cloveModalDate, setCloveModalDate] = useState<string | null>(null);

  const openCartulina = (collect_id: number, collect_name: string, date: string) => {
    setModalCollectId(collect_id);
    setModalCollectName(collect_name);
    setModalDate(date);
    setModalOpen(true);
  };

  const openCloveClients = (collect_id: number, collect_name: string, date: string) => {
    setCloveModalCollectId(collect_id);
    setCloveModalCollectName(collect_name);
    setCloveModalDate(date);
    setCloveModalOpen(true);
  };


  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });

  // datos UI
  const [rows, setRows] = useState<SimpleRow[]>([]);
  const [totals, setTotals] = useState({ balance: 0, clean_balance: 0, box: 0 });
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString; // por si ya viene YYYY-MM-DD, lo dejamos
    return d.toLocaleDateString("es-CO");
  };
  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar las oficinas.", "error");
      }
    })();
  }, [showAlert]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/collectsoffice/${filter.officeId}`);
        setCollectsOffice(data || []);
        setFilter((prev) => ({ ...prev, selectedCollects: [] }));
        setSelectAll(false);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar los cobros para la oficina.", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert]);

  // handlers
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };
  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, officeId: e.target.value }));
  };
  const handleSelectAllCollects = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSelectAll(checked);
    setFilter((prev) => ({
      ...prev,
      selectedCollects: checked ? collectsOffice.map((c) => c.id) : [],
    }));
  };
  const handleCollectChange = (collectId: number, checked: boolean) => {
    setFilter((prev) => {
      const set = new Set(prev.selectedCollects);
      if (checked) set.add(collectId); else set.delete(collectId);
      const next = Array.from(set);
      setSelectAll(next.length === collectsOffice.length && collectsOffice.length > 0);
      return { ...prev, selectedCollects: next };
    });
  };

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert("Seleccione una oficina.", "warning");
      return false;
    }
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro.", "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert("La fecha de inicio no puede ser mayor a la fecha final.", "warning");
      return false;
    }
    return true;
  };

  const baseColumns: SimpleColumn[] = [
    { field: 'collect', headerName: 'Cobro', flex: 1, minWidth: 80, maxWidth: 150, align: 'center', wrapText: true },
    { field: 'date', headerName: 'Fecha', flex: 1.2, minWidth: 60, maxWidth: 60, align: 'center' },
    { field: 'day', headerName: 'Día', flex: 1.2, minWidth: 80, maxWidth: 80, align: 'center' },
    {
      field: 'balance', headerName: 'Saldo', flex: 1, minWidth: 80, maxWidth: 80, align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : formatCurrency(Number(row.balance ?? 0)),
    },
    {
      field: 'cartel', headerName: 'Cartulina', flex: 0.8, minWidth: 80, maxWidth: 80, align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        (row as any).isGroupSeparator ? null :
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openCartulina(Number(row.collect_id ?? 0), String(row.collect ?? ''), String(row.date ?? ''));
            }}
            endIcon={<InfoOutlinedIcon fontSize="small" />}
          >
            {formatInt(Number(row.cartel ?? 0))}
          </Button>
      ),
    },
    {
      field: 'clove_client', headerName: 'Clientes Clavo', flex: 0.9, minWidth: 80, maxWidth: 80, align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => {
        if ((row as any).isGroupSeparator) {
          return null;
        }
        const count = Number(row.clove_client ?? 0);
        if (count <= 0) {
          return formatInt(count);
        }

        return (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openCloveClients(
                Number(row.collect_id ?? 0),
                String(row.collect ?? ''),
                String(row.date ?? '')
              );
            }}
            endIcon={<InfoOutlinedIcon fontSize="small" />}
          >
            {formatInt(count)}
          </Button>
        );
      },
    },
    {
      field: 'clove_value', headerName: 'Valor Clavo', flex: 1, minWidth: 80, maxWidth: 80, align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : formatCurrency(Number(row.clove_value ?? 0)),
    },
    {
      field: 'clean_balance', headerName: 'Saldo Sin Clavo', flex: 1, minWidth: 80,maxWidth:80, align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : formatCurrency(Number(row.clean_balance ?? 0)),
    },
    {
      field: 'box', headerName: 'Caja', flex: 1, minWidth: 50, maxWidth:50,align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : formatCurrency(Number(row.box ?? 0)),
    },
  ];

  const columnGroups: SimpleColumnGroup[] = [];
  const mobileColumns: SimpleColumn[] = [
    baseColumns.find(col => col.field === 'collect')!,
    baseColumns.find(col => col.field === 'date')!,
    baseColumns.find(col => col.field === 'balance')!,
    baseColumns.find(col => col.field === 'clean_balance')!,
    baseColumns.find(col => col.field === 'cartel')!,
  ];
  const displayColumns = isMobile ? mobileColumns : baseColumns;
  const displayColumnGroups: SimpleColumnGroup[] = [];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    try {
      const payload = {
        start_date: filter.startDate,        // "YYYY-MM-DD"
        end_date: filter.endDate,            // "YYYY-MM-DD"
        collect_Id: filter.selectedCollects, // [ids]
      };

      // ⬇⬇ AJUSTA este endpoint al que tengas en tu backend
      const { data } = await axios.post("/inform/summarybalance", payload);

      const items = Array.isArray(data?.data) ? data.data : [];

      if (items.length === 0) {
        setRows([]);
        setTotals({ balance: 0, clean_balance: 0, box: 0 });
        showAlert("No se encontraron registros en el período.", "info");
        return;
      }

      const mapped: SimpleRow[] = items.map((r: any, i: number) => ({
        id: `${r.collect}-${r.date}-${i}` as RowId,
        collect: r.collect ?? "",
        collect_id: r.collect_id ?? "",
        date: r.date ?? "",
        day: r.day ?? "",
        balance: Number(r.balance || 0),
        cartel: Number(r.cartel || 0),
        clove_client: Number(r.clove_client || 0),
        clove_value: Number(r.clove_value || 0),
        clean_balance: Number(r.clean_balance || 0),
        box: Number(r.box || 0),
      }));

      const sumBalance = mapped.reduce((acc, it) => acc + it.balance, 0);
      const sumClean = mapped.reduce((acc, it) => acc + it.clean_balance, 0);
      const sumBox = mapped.reduce((acc, it) => acc + it.box, 0);

      const groupedRows: SimpleRow[] = [];
      let currentCollectKey = "";

      mapped.forEach((row, index) => {
        const nextCollectKey = String(row.collect_id ?? row.collect ?? "").trim();
        if (nextCollectKey !== currentCollectKey) {
          groupedRows.push({
            id: `group-${nextCollectKey || index}` as RowId,
            collect: row.collect,
            collect_id: row.collect_id,
            date: "",
            day: "",
            balance: "",
            cartel: "",
            clove_client: "",
            clove_value: "",
            clean_balance: "",
            box: "",
            isGroupSeparator: true,
          });
          currentCollectKey = nextCollectKey;
        }

        groupedRows.push(row);
      });

      setRows(groupedRows);
      setTotals({ balance: sumBalance, clean_balance: sumClean, box: sumBox });

      showAlert(`Se encontraron ${mapped.length} registros.`, "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert("Error al obtener los datos.", "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5, pb: { xs: 10, md: 8 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Resumen Saldo</Title>
        <Box>
          <Button type="submit" variant="contained" startIcon={!isMobile && <SearchIcon />} form="form-resumen-saldo">
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>

      <Card sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <form id="form-resumen-saldo" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <Grid container spacing={2}>
              {/* Fechas */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth name="startDate" size="small" type="date" label="Fecha Inicio"
                  value={filter.startDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth name="endDate" size="small" type="date" label="Fecha Fin"
                  value={filter.endDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                />
              </Grid>

              {/* Oficina */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Oficina</InputLabel>
                  <Select value={filter.officeId} size="small" label="Oficina" onChange={handleOfficeChange}>
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={String(office.id)}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cobros */}
              {collectsOffice.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                      Seleccionar Cobros:
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={filter.selectedCollects.length === collectsOffice.length && collectsOffice.length > 0}
                          onChange={handleSelectAllCollects}
                          indeterminate={
                            filter.selectedCollects.length > 0 &&
                            filter.selectedCollects.length < collectsOffice.length
                          }
                        />
                      }
                      label="Marcar/Desmarcar todos"
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <FormGroup row>
                    {collectsOffice.map((collect) => (
                      <FormControlLabel
                        key={collect.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.selectedCollects.includes(collect.id)}
                            onChange={(e) => handleCollectChange(collect.id, e.target.checked)}
                            value={collect.id.toString()}
                          />
                        }
                        label={collect.name}
                        sx={{ minWidth: "200px", width: isMobile ? "100%" : "auto", m: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={displayColumns}
              countValue={rows.filter((row) => !(row as any).isGroupSeparator).length}
              disableInternalScroll
              showBorders
              showCount
              exportCsv
              exportFileName="resumen-saldo.csv"
              wrapCellContent
              columnGroups={displayColumnGroups}
              getRowCellSx={({ row }) =>
                (row as any).isGroupSeparator
                  ? {
                    backgroundColor: '#ffd3b8',
                    fontWeight: 700,
                  }
                  : undefined
              }
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />

      {/* Loader overlay */}
      {loading && (
        <Box sx={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.12)', zIndex: 2000
        }}>
          <CircularProgress />
        </Box>
      )}
      <CartulinaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        collectId={modalCollectId}
        date={modalDate}
        collectName={modalCollectName}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ModalCloveClients
        open={cloveModalOpen}
        onClose={() => setCloveModalOpen(false)}
        collectId={cloveModalCollectId}
        collectName={cloveModalCollectName}
        date={cloveModalDate}
        showAlert={showAlert}
      />
    </HeaderResponsive>
  );
};

export default ResumenSaldo;
